/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

'use es6';

import { AUTO_MINIMIZE_TIMEOUT, OPTIN_VIEW_CURRENT } from 'ui-addon-opt/constants/UIOptConstants';
import { useCallback, useReducer, useState } from 'react';
import { canHubOptin, canHubOptinFromOptout, canHubOptout, canUserOptin, canUserOptout, getConfirmationOptions, getLearnMoreConfig, show, showOptin, showOptout } from 'ui-addon-opt/state/OptState';
import { createInitialOptState, optStateReducer } from 'ui-addon-opt/state/OptStateReducer';
import { trackLearnMoreClick, trackOptView } from 'ui-addon-opt/lib/Tracking';
import { useFetchUserOptSettingsEffect, useInitialOptStateFetchedEffect, useOptMinimizedEffect, useOptStateChangedEffect, useTargetOptinViewChangedEffect, useUpdateUserOptSettingsEffect, useUsageTracker } from 'ui-addon-opt/effects/OptEffects';
import FormattedMessage from 'I18n/components/FormattedMessage';
import OptLoading from './OptLoading';
import OptMinimized from './OptMinimized';
import Optin from './Optin';
import Optout from './Optout';
import emptyFunction from 'react-utils/emptyFunction';
import { jsx as _jsx } from "react/jsx-runtime";
const initialOptSettings = {
  availability: {}
};
const learnMoreTextPropDefault = /*#__PURE__*/_jsx(FormattedMessage, {
  message: "ui-addon-opt.learnMoreLabel"
});

/**
 * Allows customers to opt into and out of new or beta features. Super admin users can also opt in and out all users in their hub.
 */
export const UIOpt = ({
  additionalOptinOptions,
  additionalOptoutOptions,
  learnMoreText = learnMoreTextPropDefault,
  notifyOnUserViewChangesOnly = false,
  onFetchOptError = emptyFunction,
  onInitialOptStateFetched = emptyFunction,
  onLearnMoreClick = emptyFunction,
  onOptStateChangeCancelled = emptyFunction,
  onOptStateChanged = emptyFunction,
  onTargetOptinViewChanged = emptyFunction,
  onUpdateOptError = emptyFunction,
  optInButtonText,
  OptInConfirmationModal,
  OptOutConfirmationModal,
  optinName,
  optinView = OPTIN_VIEW_CURRENT,
  optOutButtonText,
  tracker
}) => {
  const [optSettings, setOptSettings] = useState(initialOptSettings);
  const [minimized, setMinimized, setUserClickedOpt] = useOptMinimizedEffect(optinName, AUTO_MINIMIZE_TIMEOUT);
  const [opt, dispatch] = useReducer(optStateReducer, createInitialOptState());
  const ConfirmationModal = getConfirmationOptions({
    opt,
    OptInConfirmationModal,
    OptOutConfirmationModal
  });
  const usageTracker = useUsageTracker(tracker);
  const learnMore = getLearnMoreConfig(optSettings, learnMoreText);
  useFetchUserOptSettingsEffect({
    dispatch,
    optinName,
    optinView,
    onFetchOptError,
    opt,
    setOptSettings
  });
  useUpdateUserOptSettingsEffect({
    ConfirmationModal,
    dispatch,
    optinName,
    onOptStateChangeCancelled,
    onUpdateOptError,
    opt,
    optSettings
  });
  useTargetOptinViewChangedEffect({
    notifyOnUserViewChangesOnly,
    onTargetOptinViewChanged,
    opt,
    optSettings,
    optinView
  });
  useInitialOptStateFetchedEffect({
    onInitialOptStateFetched,
    optSettings
  });
  useOptStateChangedEffect({
    opt,
    onOptStateChanged,
    optinName,
    usageTracker
  });
  const onMinimizedToggledCb = useCallback(() => {
    setMinimized(isMinimized => !isMinimized);
    setUserClickedOpt();
  }, [setMinimized, setUserClickedOpt]);
  const onLearnMoreClickCb = useCallback(() => {
    onLearnMoreClick(optSettings.availability.state);
    trackLearnMoreClick(usageTracker, optinName);
  }, [onLearnMoreClick, optSettings.availability.state, optinName, usageTracker]);
  const onViewCb = useCallback(() => {
    trackOptView(usageTracker, optinName);
    setUserClickedOpt();
  }, [optinName, usageTracker, setUserClickedOpt]);
  if (!optSettings.user || !show(optSettings)) {
    return null;
  }
  if (optinView !== opt.optinView) {
    return /*#__PURE__*/_jsx(OptLoading, {});
  }
  const isOptinVisible = showOptin(optSettings);
  const isOptoutVisible = showOptout(optSettings, {
    learnMore
  });
  if (minimized && (isOptinVisible || isOptoutVisible)) {
    return /*#__PURE__*/_jsx(OptMinimized, {
      onMaximized: onMinimizedToggledCb
    });
  }
  if (isOptinVisible) {
    return /*#__PURE__*/_jsx(Optin, {
      additionalOptions: additionalOptinOptions,
      dispatch: dispatch,
      learnMore: learnMore,
      onLearnMoreClick: onLearnMoreClickCb,
      onMinimized: onMinimizedToggledCb,
      onView: onViewCb,
      optInButtonText: optInButtonText,
      showHubOptin: canHubOptin(optSettings),
      showUserOptin: canUserOptin(optSettings)
    });
  }
  if (isOptoutVisible) {
    return /*#__PURE__*/_jsx(Optout, {
      additionalOptions: additionalOptoutOptions,
      dispatch: dispatch,
      feedbackSurveyId: optSettings.availability.feedbackSurveyId,
      learnMore: learnMore,
      onLearnMoreClick: onLearnMoreClickCb,
      onMinimized: onMinimizedToggledCb,
      onView: onViewCb,
      optinName: optinName,
      optOutButtonText: optOutButtonText,
      showHubOptin: canHubOptinFromOptout(optSettings),
      showHubOptout: canHubOptout(optSettings),
      showUserOptout: canUserOptout(optSettings),
      usageTracker: usageTracker
    });
  }
  return null;
};

// PropTypes removed for React 19 compatibility. Preserved for future TS migration:
// UIOpt.propTypes = {
//   additionalOptinOptions: arrayOf(shape({ text: node.isRequired, onClick: func.isRequired })),
//   additionalOptoutOptions: arrayOf(shape({ text: node.isRequired, onClick: func.isRequired })),
//   learnMoreText: node,
//   notifyOnUserViewChangesOnly: bool,
//   onFetchOptError: func,
//   onInitialOptStateFetched: func,
//   onLearnMoreClick: func,
//   onOptStateChangeCancelled: func,
//   onOptStateChanged: func,
//   onTargetOptinViewChanged: func,
//   onUpdateOptError: func,
//   optInButtonText: node,
//   OptInConfirmationModal: func,
//   OptOutConfirmationModal: func,
//   optinName: string.isRequired,
//   optinView: oneOf([OPTIN_VIEW_NEW, OPTIN_VIEW_CURRENT]).isRequired,
//   optOutButtonText: node,
//   tracker: shape({ clone: func.isRequired, track: func.isRequired }),
// };

// Temporary for backwards compatibility
export default UIOpt;